import { getRuntime } from 'src/platform';
import { createPlatformDevice } from 'src/platform/services/device';

// eslint-disable-next-line no-control-regex
const INVALID_SERIAL_CHARACTERS = /[\u0000-\u001f\u007f]/;

type DeviceMetadataInput = {
  serialNumber?: string;
  metadata?: Record<string, unknown>;
};

/**
 * Refresh the descriptive serial at the request boundary, never the identity.
 * The source marker prevents legacy migration from using a PC name as a
 * payment-terminal serial. Refresh has no metadata field in its wire contract.
 */
export async function withWindowsDeviceSerial<T extends DeviceMetadataInput>(
  body: T,
  includeSource = false
): Promise<T> {
  if (getRuntime().kind !== 'electron') return body;

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const info = await Promise.race([
      createPlatformDevice('electron').getShellInfo(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new Error('Shell metadata unavailable')), 1500);
      }),
    ]);
    if (info.osPlatform && info.osPlatform !== 'win32') return body;

    const name = info.osPlatform === 'win32' ? info.computerName?.trim() : undefined;
    const serialNumber =
      name && name.length <= 255 && !INVALID_SERIAL_CHARACTERS.test(name) ? name : undefined;
    return {
      ...body,
      serialNumber,
      ...(includeSource && info.osPlatform === 'win32'
        ? { metadata: { ...body.metadata, serialNumberSource: 'windows-computer-name' } }
        : {}),
    };
  } catch {
    // Do not overwrite an existing PC name with a fingerprint/terminal serial
    // when the shell is unavailable. Authentication itself remains usable.
    return { ...body, serialNumber: undefined };
  } finally {
    if (timer) clearTimeout(timer);
  }
}
